import type { ReactNode } from "react"

import type { UseQueryResult } from "@tanstack/react-query"

import { getSolarProposalErrorStatus } from "~/api/hooks/useSolarProposal"

import { PINNED_BATTERY_PROPOSAL_THEME } from "../BatteryOnlyProposal/theme/battery-proposal-theme"

/** The proposal read's answers: none is retried, and each names itself. */
export const SOLAR_PROPOSAL_ERROR_MESSAGES: Readonly<Record<number, string>> = {
  401: `Redirecting to sign in`,
  403: `You don't have access to this Solar Proposal`,
  404: `Solar Proposal not found`,
}

/** A full-screen Solar route before its read answers, in the pinned theme. */
export const SolarLoadState = ({
  children,
  onRetry,
}: {
  children: ReactNode
  /** A failed read that isn't an answer (401, 403, 404) can be tried again. */
  onRetry?: () => void
}): JSX.Element => (
  <div
    data-battery-theme={PINNED_BATTERY_PROPOSAL_THEME}
    className="flex min-h-screen flex-col items-center justify-center gap-3 bg-bp-page px-6 text-center text-bp-text"
  >
    <p role="status">{children}</p>
    {onRetry ? (
      <button
        type="button"
        className="font-semibold text-bp-accent underline"
        onClick={onRetry}
      >
        Retry
      </button>
    ) : null}
  </div>
)

/** The proposal header read without a proposal to show: loading or failed. */
export const SolarProposalLoadState = ({
  query,
}: {
  query: Pick<UseQueryResult<unknown, unknown>, `error` | `isError` | `refetch`>
}): JSX.Element => {
  const answer = query.isError
    ? SOLAR_PROPOSAL_ERROR_MESSAGES[
        getSolarProposalErrorStatus(query.error) ?? 0
      ]
    : undefined
  return (
    <SolarLoadState
      onRetry={
        query.isError && answer === undefined
          ? () => void query.refetch()
          : undefined
      }
    >
      {query.isError
        ? (answer ?? `Unable to load Solar Proposal`)
        : `Loading Solar Proposal`}
    </SolarLoadState>
  )
}
